import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IDestination } from 'app/shared/model/destination.model';
import { getEntity, updateEntity, createEntity, reset } from './destination.reducer';

export const DestinationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const destinationEntity = useAppSelector(state => state.destination.entity);
  const loading = useAppSelector(state => state.destination.loading);
  const updating = useAppSelector(state => state.destination.updating);
  const updateSuccess = useAppSelector(state => state.destination.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/destination' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
        createdDate: new Date().toISOString().split('T')[0],
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
  }, []);

  useEffect(() => {
    if (destinationEntity) {
      setFormData({
        ...destinationEntity,
        createdBy: destinationEntity?.createdBy?.id,
      });
    }
  }, [destinationEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...destinationEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.destination.home.createOrEditLabel">Create or edit a Destination</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {!isNew && (
              <TextField
                label={translate('global.field.id')}
                id="destination-id"
                name="id"
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={translate('jhipsterSampleApplicationApp.destination.code')}
              id="destination-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled || false}
                  onChange={handleChange('enabled')}
                  id="destination-enabled"
                  name="enabled"
                  data-cy="enabled"
                />
              }
              label={translate('jhipsterSampleApplicationApp.destination.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.destination.defaultImage')}
              id="destination-defaultImage"
              name="defaultImage"
              data-cy="defaultImage"
              value={formData.defaultImage || ''}
              onChange={handleChange('defaultImage')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.destination.cssStyle')}
              id="destination-cssStyle"
              name="cssStyle"
              data-cy="cssStyle"
              value={formData.cssStyle || ''}
              onChange={handleChange('cssStyle')}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/destination"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DestinationUpdate;
